import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import type { ConstructionTypeData, ContractOtherCostItemData } from 'gql/graphql';
import { ContractOtherStatusEnum, ProjectMethodologyEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { EditOtherModal } from 'pages/Project/Common/Contract/OtherEdit';
import { OtherViewModal } from 'pages/Project/Common/Contract/OtherView';
import { Button, ContextMenu, IconKebab } from 'uikit';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDeleteContractOtherCost } from './hooks/useDeleteContractOtherCost';

const getItemLabel = R.prop('label');

interface IContractOthersRowMenu {
    row?: ContractOtherCostItemData;
    onOperationSuccess: (id?: number) => void;
    constructionTypes: ConstructionTypeData[];
    contractId: number;
    constructionTypeId: number;
    hasWorkOrders: boolean;
    methodology: ProjectMethodologyEnum;
    costRounding: number;
}

const deleteHeader = 'УДАЛЕНИЕ ПРОЧИХ ЗАТРАТ';

export const ContractOthersRowMenu: React.FC<IContractOthersRowMenu> = (props) => {
    const {
        row,
        onOperationSuccess,
        constructionTypes,
        contractId,
        constructionTypeId,
        hasWorkOrders,
        methodology,
        costRounding,
    } = props;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const ref = useRef(null);
    const { contract: contractRules } = useAccess();

    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);

    const [isModify, setModify] = useState(false);

    const handleOnClick = useCallback(() => {
        setIsOpen(R.not);
    }, []);

    const { makeRequest, reset, data, status, error } = useDeleteContractOtherCost({
        methodology,
        contractOtherCostId: row?.id,
    });

    useEffect(() => {
        if (data?.result && status === RequestStatus.SUCCESS) {
            onOperationSuccess(data?.id);
            setIsConfirmationDialogOpen(false);
            reset();
        }
    }, [data?.id, data?.result, onOperationSuccess, reset, status]);

    const isOnlyTypeEditAvailable = !isUer && hasWorkOrders;

    const items = useMemo(() => {
        const canDeleteOtherUer = row?.canDelete;
        const canDeleteOtherBim =
            (row?.status === ContractOtherStatusEnum.ContractCondition && !hasWorkOrders) ||
            (row?.status === ContractOtherStatusEnum.NewRecord &&
                !row?.isIncludedInActKc2 &&
                !row?.isIncludedInWorkOrder);

        const canEditUer = row?.canEdit;
        const canEditBim = !hasWorkOrders && !row?.isIncludedInWorkOrder;

        const isEditBimDisabled = !canEditBim || !contractRules?.canEdit;

        if (isUer) {
            return [
                ...(canEditUer
                    ? [
                          {
                              label: 'Редактировать',
                              action: () => {
                                  setIsEditDialogOpen(true);
                              },
                              disabled: !contractRules?.canEdit,
                          },
                      ]
                    : [
                          {
                              label: 'Редактировать',
                              action: () => {
                                  setModify(true);
                                  setIsEditDialogOpen(true);
                              },
                              disabled: !contractRules?.canEdit,
                          },
                      ]),
                {
                    label: 'Удалить',
                    action: () => {
                        setIsConfirmationDialogOpen(true);
                    },
                    disabled: !canDeleteOtherUer || !contractRules?.canDelete,
                },
            ];
        }

        return [
            ...(hasWorkOrders
                ? [
                      {
                          label: 'Изменить прочую',
                          action: () => {
                              setModify(true);
                              setIsEditDialogOpen(true);
                          },
                      },
                  ]
                : []),
            ...(isEditBimDisabled
                ? [
                      {
                          label: 'Просмотреть',
                          action: () => {
                              setIsViewDialogOpen(true);
                          },
                      },
                  ]
                : [
                      {
                          label: 'Редактировать',
                          action: () => {
                              setIsEditDialogOpen(true);
                          },
                      },
                  ]),
            {
                label: 'Удалить',
                action: () => {
                    setIsConfirmationDialogOpen(true);
                },
                disabled: !canDeleteOtherBim || !contractRules?.canDelete,
            },
        ];
    }, [
        contractRules?.canDelete,
        contractRules?.canEdit,
        hasWorkOrders,
        isUer,
        row?.canDelete,
        row?.canEdit,
        row?.isIncludedInActKc2,
        row?.isIncludedInWorkOrder,
        row?.status,
    ]);

    const onItemOnClick = useCallback(
        (item) => () => {
            item.action();
            setIsOpen(false);
        },
        [],
    );

    const onCloseDeleteDialog = useCallback(() => {
        setIsConfirmationDialogOpen(false);
    }, []);

    const onDeleteConfirmed = useCallback(() => {
        makeRequest();
        setIsSubmitted(true);
    }, [makeRequest]);

    const onCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const onCreateReportDownload = useCallback(() => {
        ExportCSVReport(data?.validation);
    }, [data?.validation]);

    const onEditModalClose = useCallback(
        (isSuccess) => {
            setIsEditDialogOpen(false);

            if (isSuccess) {
                onOperationSuccess();
            }
        },
        [onOperationSuccess],
    );

    const onViewModalClose = useCallback(() => {
        setIsViewDialogOpen(false);
    }, []);

    return (
        <div
            style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Button
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                style={{ marginTop: 12, marginBottom: 12, marginLeft: 4 }}
                onClick={handleOnClick}
                ref={ref}
            />

            <ContextMenu
                isOpen={isOpen}
                items={items}
                getItemLabel={getItemLabel}
                anchorRef={ref}
                direction="downStartLeft"
                getItemOnClick={onItemOnClick}
                onClickOutside={handleOnClick}
            />
            <ConfirmationDialog
                textHeader={deleteHeader}
                textConfirmation="Вы уверены, что хотите удалить записи?"
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
                isOpened={isConfirmationDialogOpen}
                onSubmit={onDeleteConfirmed}
                onClose={onCloseDeleteDialog}
            />
            <ErrorHandlingModal
                titleText={deleteHeader}
                isSubmitted={isSubmitted}
                statusRequest={status}
                dataRequest={data}
                errorsRequest={error}
                handleCloseModalError={onCloseModalError}
                handleCreateReport={onCreateReportDownload}
            />
            {isEditDialogOpen && (
                <EditOtherModal
                    onClose={onEditModalClose}
                    contractId={contractId}
                    constructionTypeId={constructionTypeId}
                    constructionTypes={constructionTypes}
                    otherIdProp={row?.id}
                    methodology={methodology}
                    isOnlyTypeEditAvailable={isOnlyTypeEditAvailable}
                    isModify={isModify}
                    isNew={methodology === ProjectMethodologyEnum.Uer}
                />
            )}
            {isViewDialogOpen && (
                <OtherViewModal
                    constructionTypes={constructionTypes}
                    contractId={contractId}
                    contractOtherCostId={row?.id}
                    methodology={methodology}
                    onClose={onViewModalClose}
                    costRounding={costRounding}
                />
            )}
        </div>
    );
};

ContractOthersRowMenu.displayName = 'ContractOthersRowMenu';
